// LIBS
import React, { createRef, useMemo } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import Cookies from "js-cookie";
import { useSelector } from "react-redux";

// UTILITIES
import { withABTestWrapper } from "@lululemon/mwa-ab-testing/esm";
import { ToastProvider } from "@lululemon/ui-library";

// COMPONENTS
import ProductTile from "@lululemon/ecom-common/src/components/product-tile/product-tile";
import {
  getProductAndPromoList,
  getBraQuizResultPillLabel,
  getNormalizedProductDetailsList,
} from "./product-list-util";
import { createErrorBoundaryWrapper } from "@lululemon/ecom-cdp/src/lib/cdp-error-boundaries";
import { default as PromoTile } from "../promo-tile/promo-tile";
import EmptySearchResults from "../empty-search-results/empty-search-results";
import { MemoizedProductTilePlaceholder } from "../placeholder/placeholder";
import { useNewBOPIS } from "@lululemon/ecom-pdp/src/hooks/use-new-bopis";
import SelectiveComparisonDrawer from "@lululemon/ecom-selective-comparison/src/components/selective-comparison-drawer/selective-comparison-drawer";

// STYLES
import styles from "./product-list.module.scss";

// HOOKS
import useSelectiveComparison from "@lululemon/ecom-selective-comparison/src/hooks/useSelectiveComparison";

const ProductList = ({
  asPath,
  refresh,
  currentColorFilters,
  currentSizeFilters,
  bp,
  showBopis,
  isMobileDevice,
  showAllProducts,
  bopis,
  searchTerm,
  products,
  showHoverImage,
  inventory,
  isMobile,
  promo,
  bopisProducts,
  onClickProductTile,
  categoryTitle,
  graphqlClient,
  productHighlightDetails,
  filteredSizes: braQuizResults,
  productDetailsList,
  truefitAtCDPABFlag,
}) => {
  const productListRef = createRef();
  const locale = Cookies.get("UsrLocale") || "";

  const {
    shouldDisplaySelectiveComparison,
    productsToCompare,
    maxProductsToCompare,
    updateProductsToCompare,
    clearAllProductsToCompare,
    removeProductToCompareById,
    unFocusComparisonDrawer,
    comparisonDrawerRef,
    focusComparisonDrawer,
    allowScrollMobile,
  } = useSelectiveComparison(asPath, categoryTitle, isMobile);

  const bopisFulfillmentAB = useNewBOPIS();
  const totalNumRecs = products?.length;
  const productProps = {
    products: showBopis ? bopisProducts : products,
    bp,
    promo,
    totalNumRecs,
  };

  const normalizedProductDetailsList = useMemo(
    () => getNormalizedProductDetailsList(productDetailsList),
    [productDetailsList]
  );

  const productList = getProductAndPromoList(productProps);

  const { wishlists: state, footer } = useSelector(
    (state) => state?.rootReducer
  );
  const { wishlists } = state || [];

  if (refresh) {
    return <MemoizedProductTilePlaceholder amount={totalNumRecs} />;
  }

  let promoTileIndex = 0;

  return (
    <>
      <ToastProvider>
        <div
          data-testid="product-list"
          className={styles.productList}
          ref={productListRef}
        >
          <h2 className="sr-only">Product List</h2>
          <div className="row">
            {productList.length > 0 ? (
              productList.map((product, index) => {
                const isPromoTile =
                  product?.sys?.contentType?.sys?.id === "webCdpGridItem";
                if (isPromoTile) {
                  return (
                    <PromoTile
                      containerClass={styles.productListItem}
                      gridItemData={product}
                      locale={locale}
                      index={promoTileIndex++}
                      key={`${product?.sys?.id}-${promoTileIndex}`}
                      truefitAtCDPABFlag={truefitAtCDPABFlag}
                    />
                  );
                }

                const inventoryId =
                  product["product-id"] || product["repository-id"];
                const productDetailsContentItem =
                  normalizedProductDetailsList?.get(product["product-id"]);
                const productDetail = productDetailsContentItem?.productDetails;

                return (
                  <>
                    {product["sku-style-order"]?.length > 0 && (
                      <div
                        className={classNames(
                          styles.productListItem,
                          "col-xs-6",
                          "col-sm-4"
                        )}
                        key={`${inventoryId}`}
                        data-testid="product-tile"
                      >
                        <ProductTile
                          bp={bp}
                          categoryTitle={categoryTitle}
                          cdpSerpSwatch
                          currentColorFilters={currentColorFilters}
                          currentSizeFilters={currentSizeFilters}
                          isMobileDevice={isMobileDevice}
                          showHoverImage={showHoverImage}
                          sizePill={getBraQuizResultPillLabel(
                            product,
                            braQuizResults
                          )}
                          onClick={onClickProductTile}
                          skus={inventory?.[inventoryId]}
                          showBopis={showBopis}
                          product={product}
                          productIndex={index}
                          updateProductsToCompare={updateProductsToCompare}
                          shouldDisplaySelectiveComparison={
                            shouldDisplaySelectiveComparison
                          }
                          productHighlightDetails={productHighlightDetails}
                          wishlists={wishlists}
                          graphqlClient={graphqlClient}
                          productCount={totalNumRecs}
                          productDetail={productDetail}
                          bopisFulfillmentAB={bopisFulfillmentAB}
                          productsToCompare={productsToCompare}
                          maxProductsToCompare={maxProductsToCompare}
                          focusComparisonDrawer={focusComparisonDrawer}
                        />
                      </div>
                    )}
                  </>
                );
              })
            ) : (
              <EmptySearchResults
                bopis={bopis}
                showAllProducts={showAllProducts || null}
                searchTerm={searchTerm}
              />
            )}
          </div>
          <SelectiveComparisonDrawer
            isAboveFooterWaypoint={footer ? footer?.isAboveWaypoint : undefined}
            shouldDisplaySelectiveComparison={shouldDisplaySelectiveComparison}
            clearAllProductsToCompare={clearAllProductsToCompare}
            maxProductsToCompare={maxProductsToCompare}
            productsToCompare={productsToCompare}
            removeProductToCompareById={removeProductToCompareById}
            unFocusComparisonDrawer={unFocusComparisonDrawer}
            comparisonDrawerRef={comparisonDrawerRef}
            cdpUrl={asPath}
            cdpLabel={categoryTitle}
            allowScrollMobile={allowScrollMobile}
            isMobile={isMobile}
          />
        </div>
      </ToastProvider>
    </>
  );
};

ProductList.defaultProps = {
  error: undefined,
  promo: undefined,
  showAllProducts: undefined,
  filteredSizes: undefined,
  refresh: false,
  showHoverImage: false,
  onClickProductTile: () => {},
  inventory: undefined,
  graphqlClient: undefined,
  bp: undefined,
  isMobile: false,
  isMobileDevice: false,
  productHighlightDetails: {},
  productDetailsList: [],
  truefitAtCDPABFlag: false,
};

ProductList.propTypes = {
  asPath: PropTypes.string,
  bopis: PropTypes.bool.isRequired,
  bp: PropTypes.string,
  promo: PropTypes.arrayOf(PropTypes.object),
  filteredSizes: PropTypes.shape({}),
  refresh: PropTypes.bool,
  currentColorFilters: PropTypes.arrayOf(PropTypes.string).isRequired,
  currentSizeFilters: PropTypes.arrayOf(PropTypes.string).isRequired,
  error: PropTypes.shape({}),
  inventory: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.object)),
  isMobile: PropTypes.bool,
  isMobileDevice: PropTypes.bool,
  products: PropTypes.arrayOf(PropTypes.object).isRequired,
  searchTerm: PropTypes.string.isRequired,
  showAllProducts: PropTypes.bool,
  showBopis: PropTypes.bool.isRequired,
  showHoverImage: PropTypes.bool,
  onClickProductTile: PropTypes.func,
  categoryTitle: PropTypes.string.isRequired,
  bopisProducts: PropTypes.arrayOf(PropTypes.object).isRequired,
  graphqlClient: PropTypes.shape({}),
  productHighlightDetails: PropTypes.shape({}),
  productDetailsList: PropTypes.arrayOf(PropTypes.object),
  truefitAtCDPABFlag: PropTypes.bool,
};

export const productListWithABTest = withABTestWrapper({
  name: "product-list",
  abTestEndDate: "06/01/2025",
})(ProductList);

export const CDPProductListWithErrorBoundary = createErrorBoundaryWrapper(
  productListWithABTest,
  "ProductList"
);

export default ProductList;
